import { lazy, Suspense, ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrderProvider } from "@/contexts/OrderContext";
import PageTransition from "./components/PageTransition";
import { usePageTracking } from "@/hooks/usePageTracking";
import { lazyRetry } from "@/lib/lazyRetry";

const ProtectedRoute = lazy(() => lazyRetry(() => import("./components/ProtectedRoute")));
const AdminRoute = lazy(() => lazyRetry(() => import("./components/AdminRoute")));
const SessionTimeoutGuard = lazy(() => lazyRetry(() => import("./components/SessionTimeoutGuard")));

const Toaster = lazy(() => lazyRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster }))));
const Sonner = lazy(() => lazyRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster }))));

const LazyTooltipProvider = lazy(() =>
  lazyRetry(() => import("@/components/ui/tooltip").then(m => ({
    default: ({ children }: { children: ReactNode }) => (
      <m.TooltipProvider>{children}</m.TooltipProvider>
    ),
  })))
);

const Chatbot = lazy(() => lazyRetry(() => import("./components/Chatbot")));

import Index from "./pages/Index";

const NotFound = lazy(() => lazyRetry(() => import("./pages/NotFound")));
const Login = lazy(() => lazyRetry(() => import("./pages/Login")));
const Register = lazy(() => lazyRetry(() => import("./pages/Register")));
const ForgotPassword = lazy(() => lazyRetry(() => import("./pages/ForgotPassword")));
const ResetPassword = lazy(() => lazyRetry(() => import("./pages/ResetPassword")));
const Dashboard = lazy(() => lazyRetry(() => import("./pages/Dashboard")));
const Profile = lazy(() => lazyRetry(() => import("./pages/Profile")));
const Admin = lazy(() => lazyRetry(() => import("./pages/Admin")));
const CouponAdmin = lazy(() => lazyRetry(() => import("./pages/CouponAdmin")));
const Terms = lazy(() => lazyRetry(() => import("./pages/Terms")));
const Privacy = lazy(() => lazyRetry(() => import("./pages/Privacy")));
const OrderForm = lazy(() => lazyRetry(() => import("./pages/OrderForm")));
const ProjectF = lazy(() => lazyRetry(() => import("./pages/ProjectF")));
const ProjectD = lazy(() => lazyRetry(() => import("./pages/ProjectD")));
const ClientPortal = lazy(() => lazyRetry(() => import("./pages/ClientPortal")));
const Pricing = lazy(() => lazyRetry(() => import("./pages/Pricing")));

const queryClient = new QueryClient();

// Animated routes wrapper that uses location for AnimatePresence
function AnimatedRoutes() {
  const location = useLocation();
  usePageTracking();

  const isProjectF = location.pathname.startsWith('/project-f');
  const isProjectD = location.pathname === '/project-d';

  // Project F/D have their own layouts, render outside AnimatePresence
  if (isProjectF || isProjectD) {
    return (
      <Suspense fallback={null}>
        <Routes location={location}>
          <Route path="/project-f/*" element={
            <AdminRoute>
              <ProjectF />
            </AdminRoute>
          } />
          <Route path="/project-d" element={
            <AdminRoute>
              <ProjectD />
            </AdminRoute>
          } />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <PageTransition>
                <Index />
              </PageTransition>
            }
          />
          <Route
            path="/login"
            element={
              <PageTransition>
                <Login />
              </PageTransition>
            }
          />
          <Route
            path="/register"
            element={
              <PageTransition>
                <Register />
              </PageTransition>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PageTransition>
                <ForgotPassword />
              </PageTransition>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PageTransition>
                <ResetPassword />
              </PageTransition>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <Dashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <Profile />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <PageTransition>
                  <Admin />
                </PageTransition>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/coupons"
            element={
              <AdminRoute>
                <PageTransition>
                  <CouponAdmin />
                </PageTransition>
              </AdminRoute>
            }
          />
          <Route
            path="/terms"
            element={
              <PageTransition>
                <Terms />
              </PageTransition>
            }
          />
          <Route
            path="/privacy"
            element={
              <PageTransition>
                <Privacy />
              </PageTransition>
            }
          />
          <Route
            path="/pricing"
            element={
              <PageTransition>
                <Pricing />
              </PageTransition>
            }
          />
          <Route
            path="/order"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <OrderForm />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <ClientPortal />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route
            path="*"
            element={
              <PageTransition>
                <NotFound />
              </PageTransition>
            }
          />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OrderProvider>
          <Suspense fallback={null}>
            <LazyTooltipProvider>
              <Suspense fallback={null}>
                <Toaster />
                <Sonner />
              </Suspense>
              <BrowserRouter>
                <Suspense fallback={null}>
                  <SessionTimeoutGuard />
                  <Chatbot />
                </Suspense>
                <div>
                  <AnimatedRoutes />
                </div>
              </BrowserRouter>
            </LazyTooltipProvider>
          </Suspense>
        </OrderProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
